import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import * as XLSX from 'xlsx';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';
import {
  OPTIONAL_COLUMNS,
  REQUIRED_COLUMNS,
  TEMPLATE_HEADERS,
  buildPropertyImportRows,
} from './propertyImportUtils';

const UploadArea = styled(Box)(() => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: ht.cardRadius,
  padding: '24px 16px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.blue,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const parseWorkbookToGrid = (workbook) => {
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const worksheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false });
};

const PropertyImportDialog = ({
  open,
  onClose,
  tenantId,
  username,
  propertyGroups,
  onImportSuccess,
  showSuccess,
  showError,
}) => {
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [headerError, setHeaderError] = useState('');
  const [rowErrors, setRowErrors] = useState([]);
  const [payload, setPayload] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setFileName('');
    setParseError('');
    setHeaderError('');
    setRowErrors([]);
    setPayload([]);
    setLastResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onClose();
  };

  const applyGrid = useCallback(
    (grid) => {
      if (!grid.length) {
        setParseError('The file is empty.');
        setHeaderError('');
        setRowErrors([]);
        setPayload([]);
        return;
      }
      const headerRow = grid[0].map((cell) => (cell === null || cell === undefined ? '' : cell));
      const dataRows = grid
        .slice(1)
        .filter((row) => row.some((cell) => String(cell ?? '').trim() !== ''));
      const parsed = buildPropertyImportRows(headerRow, dataRows, {
        tenantId,
        username,
        propertyGroups,
      });
      setHeaderError(parsed.headerError || '');
      setRowErrors(parsed.rowErrors || []);
      setPayload(parsed.payload || []);
      setParseError(
        !parsed.headerError && dataRows.length === 0 ? 'No data rows found after the header row.' : ''
      );
    },
    [propertyGroups, tenantId, username]
  );

  const processFile = useCallback(
    (file) => {
      if (!file) return;
      resetState();
      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: false });
          applyGrid(parseWorkbookToGrid(workbook));
        } catch (err) {
          console.error(err);
          setParseError(err.message || 'Could not read the file. Use .xlsx, .xls, or .csv.');
        }
      };
      reader.onerror = () => setParseError('Failed to read the file.');
      reader.readAsArrayBuffer(file);
    },
    [applyGrid, resetState]
  );

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const downloadTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Properties');
    XLSX.writeFile(workbook, 'property-upload-template.xlsx');
  };

  const handleSubmit = async () => {
    if (!payload.length || headerError || rowErrors.length || submitting) return;
    setSubmitting(true);
    setLastResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/bulk`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || result.message || `Import failed: ${response.status}`);
      }

      setLastResult(result);
      const succeeded = Array.isArray(result.succeeded) ? result.succeeded : [];
      const failCount = result.failureCount ?? (Array.isArray(result.failed) ? result.failed.length : 0);
      const okCount = result.successCount ?? succeeded.length;

      if (okCount > 0 && onImportSuccess) {
        onImportSuccess();
      }

      if (failCount === 0) {
        (showSuccess || (() => {}))(`Imported ${okCount} property(ies) successfully`);
        resetState();
        onClose();
      } else {
        (showError || (() => {}))(
          `Imported with ${okCount} succeeded and ${failCount} failed — see details below.`
        );
      }
    } catch (err) {
      console.error(err);
      (showError || (() => {}))(err.message || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const canImport = payload.length > 0 && !headerError && rowErrors.length === 0 && !parseError;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        ...harmonateDialogPaperProps,
        sx: { ...harmonateDialogPaperProps.sx, maxHeight: '95vh' },
      }}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: ht.white }}>
          Upload properties
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small" aria-label="close" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ ...harmonateDialogContentSx, display: 'flex', flexDirection: 'column', gap: 0 }}>
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: ht.listBg,
            border: `1px solid ${ht.line}`,
            borderRadius: ht.fieldRadius,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Column headers (first row)
            </Typography>
            <Button size="small" variant="outlined" onClick={downloadTemplate} disabled={submitting} sx={harmonateUploadOutlinedSx}>
              Download template
            </Button>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Required:</strong> {REQUIRED_COLUMNS.map((column) => column.display).join(', ')}
          </Typography>
          <Typography variant="body2">
            <strong>Optional:</strong> {OPTIONAL_COLUMNS.map((column) => column.display).join(', ')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Property Group can be an existing group name. Yes/No columns accept Yes or No. Format Property Code as text so long codes are not rounded.
          </Typography>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Upload file
        </Typography>
        <DialogContentText sx={{ mb: 2, color: 'text.secondary' }}>
          Drag and drop an Excel or CSV file here, or click to browse. Each data row needs a property name and property code.
        </DialogContentText>

        <HiddenInput
          ref={fileInputRef}
          id="property-import-file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileInputChange}
        />
        <label
          htmlFor="property-import-file-input"
          style={{
            display: 'block',
            cursor: submitting ? 'default' : 'pointer',
            marginBottom: 16,
          }}
        >
          <UploadArea
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              borderColor: isDragging ? ht.green : undefined,
              backgroundColor: isDragging ? ht.secondaryTab : undefined,
              pointerEvents: submitting ? 'none' : 'auto',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? ht.green : ht.blue, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Drop file here or click to browse
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supports .xlsx, .xls, .csv
            </Typography>
          </UploadArea>
        </label>

        {fileName ? (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: ht.listBg,
              border: `1px solid ${ht.line}`,
              borderRadius: ht.fieldRadius,
            }}
          >
            <InsertDriveFileIcon color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap title={fileName}>
                {fileName}
              </Typography>
              {payload.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {payload.length} property row(s) ready to import
                </Typography>
              ) : null}
            </Box>
            <Button size="small" variant="outlined" onClick={resetState} disabled={submitting} sx={harmonateUploadOutlinedSx}>
              Clear
            </Button>
          </Paper>
        ) : null}

        {parseError ? (
          <Typography color="error" variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
            {parseError}
          </Typography>
        ) : null}
        {headerError ? (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {headerError}
          </Typography>
        ) : null}
        {rowErrors.length > 0 ? (
          <Typography color="error" variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
            {`Import blocked — fix the file and try again.\n${rowErrors.join('\n')}`}
          </Typography>
        ) : null}

        {lastResult ? (
          <Box sx={{ border: `1px solid ${ht.line}`, borderRadius: ht.fieldRadius, p: 1.5, mt: 1, bgcolor: ht.listBg }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Result: {lastResult.successCount ?? 0} succeeded, {lastResult.failureCount ?? 0} failed
            </Typography>
            {lastResult.failed?.length > 0 ? (
              <List dense disablePadding>
                {lastResult.failed.map((failure, index) => (
                  <ListItem key={`${failure.index}-${index}`} disableGutters sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={`Row ${typeof failure.index === 'number' ? failure.index + 2 : '—'} — ${failure.propertyCode || 'unknown code'}`}
                      secondary={failure.error}
                    />
                  </ListItem>
                ))}
              </List>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canImport || submitting}
          sx={harmonateProceedButtonSx}
        >
          {submitting ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyImportDialog;
